import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, Globe, AlertCircle } from 'lucide-react';
import { accessDBApi } from '../../../services/api';
import { Button } from '../../../components/ui/button';

interface DiscoveredUrl {
  url: string;
  source: 'sitemap' | 'crawl';
}

interface CrawlProgress {
  url: string;
  status: 'pending' | 'processing' | 'completed' | 'error' | 'skipped';
}

interface Props {
  domainId: string;
  domainUrl: string;
  onComplete: () => void;
  onSkip?: () => void;
  initialUrls?: DiscoveredUrl[];
}

type Phase = 'discovering' | 'selecting' | 'empty' | 'crawling' | 'done' | 'error';

export const UrlDiscovery: React.FC<Props> = ({ domainId, onComplete, onSkip, initialUrls }) => {
  const [phase, setPhase] = useState<Phase>('discovering');
  const [discoveredUrls, setDiscoveredUrls] = useState<DiscoveredUrl[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [crawlProgresses, setCrawlProgresses] = useState<CrawlProgress[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const URL_AUTO_SELECT_THRESHOLD = 50;

  useEffect(() => {
    if (initialUrls) {
      setDiscoveredUrls(initialUrls);
      if (initialUrls.length === 0) {
        setPhase('empty');
      } else {
        if (initialUrls.length <= URL_AUTO_SELECT_THRESHOLD) {
          setSelectedUrls(new Set(initialUrls.map(u => u.url)));
        } else {
          setSelectedUrls(new Set());
        }
        setPhase('selecting');
      }
      return;
    }
    const discoverUrls = async () => {
      try {
        const res = await accessDBApi.post(`/v1/domains/${domainId}/discover-urls`, {});
        const urls: DiscoveredUrl[] = res.data?.data?.urls ?? res.data?.urls ?? [];
        if (urls.length === 0) {
          setPhase('empty');
        } else {
          setDiscoveredUrls(urls);
          if (urls.length <= URL_AUTO_SELECT_THRESHOLD) {
            setSelectedUrls(new Set(urls.map((u) => u.url)));
          } else {
            setSelectedUrls(new Set());
          }
          setPhase('selecting');
        }
      } catch {
        setPhase('error');
        setErrorMsg('URL探索に失敗しました');
      }
    };
    discoverUrls();
  }, [domainId, initialUrls]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUrls(new Set(discoveredUrls.map((u) => u.url)));
    } else {
      setSelectedUrls(new Set());
    }
  };

  const handleToggleUrl = (url: string) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  };

  const handleStartLearning = async () => {
    const urls = Array.from(selectedUrls);
    const progresses: CrawlProgress[] = urls.map((url) => ({ url, status: 'pending' }));
    setCrawlProgresses(progresses);
    setPhase('crawling');

    try {
      const BATCH_SIZE = 200;
      for (let i = 0; i < urls.length; i += BATCH_SIZE) {
        const chunk = urls.slice(i, i + BATCH_SIZE);
        await accessDBApi.post(`/v1/domains/${domainId}/crawl/batch`, { urls: chunk });
      }
    } catch {
      // バッチ起動失敗でもポーリング継続
    }

    const crawlObserved = { current: false };

    const poll = setInterval(async () => {
      try {
        const res = await accessDBApi.get(`/v1/domains/${domainId}`);
        const domain = res.data?.data ?? {};
        const files: Array<{ url?: string; sync_status?: string }> = domain.crawl_files ?? [];
        const domainSyncStatus: string = domain.sync_status ?? '';
        setCrawlProgresses((prev) => {
          const updated = prev.map((p) => {
            const file = files.find((f) => f.url === p.url);
            if (!file) return { ...p, status: 'skipped' as const }; // BE削除(4xx skip)=skipped扱い
            const rawStatus = file.sync_status ?? '';
            const status = (rawStatus === 'synced' ? 'completed' : rawStatus) as CrawlProgress['status'] || p.status;
            return { ...p, status };
          });

          // crawlObserved: クロールが実際に始まったことを観測（クロール前の古いsynced状態で誤完了防止）
          if (!crawlObserved.current) {
            const anyUrlObserved = prev.some((p) => files.some((f) => f.url === p.url));
            const anyInProgress = files.some((f) => f.sync_status === 'pending' || f.sync_status === 'syncing');
            if (anyUrlObserved || anyInProgress) {
              crawlObserved.current = true;
            }
          }

          // 完了判定(A): 送信URL全てが終端到達（completed/error/skipped）
          const filesAllTerminal =
            updated.length > 0 &&
            updated.every((p) => p.status === 'completed' || p.status === 'error' || p.status === 'skipped');
          // 完了判定(B): domain権威ステータスがsynced/error かつ pending/syncingファイルなし
          const domainTerminal =
            crawlObserved.current &&
            (domainSyncStatus === 'synced' || domainSyncStatus === 'error') &&
            !files.some((f) => f.sync_status === 'pending' || f.sync_status === 'syncing');
          if (filesAllTerminal || domainTerminal) {
            clearInterval(poll);
            setTimeout(() => setPhase('done'), 0);
          }
          return updated;
        });
      } catch {
        // ポーリングエラーは無視
      }
    }, 5000);
  };

  const pathGroups = useMemo(() => {
    const groups = new Map<string, string[]>();
    for (const item of discoveredUrls) {
      try {
        const path = new URL(item.url.startsWith('http') ? item.url : `https://${item.url}`).pathname;
        const segments = path.split('/').filter(Boolean);
        if (segments.length >= 2) {
          const prefix = '/' + segments[0] + '/';
          if (!groups.has(prefix)) groups.set(prefix, []);
          groups.get(prefix)!.push(item.url);
        }
      } catch { /* skip */ }
    }
    return Array.from(groups.entries())
      .filter(([, groupUrls]) => groupUrls.length >= 3)
      .sort((a, b) => b[1].length - a[1].length);
  }, [discoveredUrls]);

  if (phase === 'discovering') {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-sm text-text-secondary">URLを探索中...</p>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-8 w-8 text-red-500 mb-4" />
        <p className="text-sm text-red-500 mb-4">{errorMsg}</p>
        {onSkip && (
          <Button variant="outline" data-testid="skip-discovery-btn" onClick={onSkip} disabled={(phase as string) === 'crawling'}>
            スキップ
          </Button>
        )}
      </div>
    );
  }

  if (phase === 'empty') {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Globe className="h-8 w-8 text-text-secondary mb-4" />
        <p className="text-sm text-text-secondary mb-6" data-testid="url-discovery-empty-message">
          URLが見つかりませんでした
        </p>
        {onSkip && (
          <div className="flex gap-3">
            <Button variant="outline" data-testid="manual-add-btn" onClick={onSkip} disabled={(phase as string) === 'crawling'}>
              手動で追加する
            </Button>
            <Button variant="outline" data-testid="skip-discovery-btn" onClick={onSkip} disabled={(phase as string) === 'crawling'}>
              スキップ
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (phase === 'crawling') {
    const completedCount = crawlProgresses.filter((p) => p.status === 'completed').length;
    const totalCount = crawlProgresses.length;
    const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
    return (
      <div className="bg-white rounded-xl border border-border p-8 flex flex-col items-center text-center">
        <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: '#E0F2F1', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#0a7a72' }} />
        </div>
        <p className="text-base font-bold text-text-primary mb-1">学習中…</p>
        <p className="text-sm text-text-secondary mb-6">ページの内容を解析しています</p>
        <div className="w-full" data-testid="crawl-progress">
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div className="h-2 rounded-full transition-all" style={{ width: `${progressPct}%`, backgroundColor: '#0a7a72' }} />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-text-secondary">{completedCount} / {totalCount} ページ完了</p>
            <p className="text-xs font-bold" style={{ color: '#0a7a72' }}>{progressPct}%</p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'done') {
    const completedCount = crawlProgresses.filter((p) => p.status === 'completed').length;
    return (
      <div className="bg-white rounded-xl border border-border p-8 flex flex-col items-center text-center">
        {/* 完了チェックアイコン */}
        <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#0a7a72" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '36px', height: '36px' }}>
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <p className="text-base font-bold text-text-primary mb-2">
          {completedCount > 0 ? `${completedCount}ページ` : ''}の学習が完了しました
        </p>
        <p className="text-sm text-text-secondary mb-8">
          さっそく動作をためすか、サイトに設置してチャットボットを公開しましょう。
        </p>
        <div className="flex items-center gap-3">
          {onSkip && (
            <Button
              variant="outline"
              data-testid="start-learning-btn"
              onClick={onSkip}
            >
              動作をためす
            </Button>
          )}
          <Button
            onClick={onComplete}
            style={{ backgroundColor: '#0a7a72', color: '#fff' }}
          >
            サイトに設置する
          </Button>
        </div>
      </div>
    );
  }

  // selecting phase
  const allSelected = discoveredUrls.length > 0 && selectedUrls.size === discoveredUrls.length;

  const sourceLabel = (() => {
    const hasSitemap = discoveredUrls.some((u) => u.source === 'sitemap');
    const hasCrawl = discoveredUrls.some((u) => u.source === 'crawl');
    if (hasSitemap && hasCrawl) return 'sitemap + クロールで検出';
    if (hasSitemap) return 'sitemapで検出';
    return 'クロールで検出';
  })();

  const getUrlDisplay = (url: string): { name: string; path: string } => {
    try {
      const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
      const path = parsed.pathname;
      const segments = path.split('/').filter(Boolean);
      const name = segments.length > 0 ? decodeURIComponent(segments[segments.length - 1]) : parsed.hostname;
      return { name, path: path || '/' };
    } catch {
      return { name: url, path: '' };
    }
  };

  return (
    <div>
      {/* すべて選択ヘッダ行 */}
      <div className="flex items-center gap-3 mb-0 px-4 py-3 bg-gray-50 rounded-t-lg border border-border">
        <input
          type="checkbox"
          data-testid="select-all-urls"
          checked={allSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="h-4 w-4 cursor-pointer flex-shrink-0"
          style={{ accentColor: '#0a7a72' }}
        />
        <span className="text-sm font-medium">すべて選択</span>
        <span className="text-xs text-text-secondary">（{selectedUrls.size} / {discoveredUrls.length} 選択中）</span>
        <span className="ml-auto text-xs text-text-secondary flex-shrink-0">{sourceLabel}</span>
      </div>

      {/* パスパターン一括操作チップ */}
      {pathGroups.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3 px-4 pt-3">
          {pathGroups.map(([prefix, groupUrls]) => {
            const allGroupSelected = groupUrls.every(u => selectedUrls.has(u));
            return (
              <button
                key={prefix}
                onClick={() => {
                  setSelectedUrls(prev => {
                    const next = new Set(prev);
                    if (allGroupSelected) {
                      groupUrls.forEach(u => next.delete(u));
                    } else {
                      groupUrls.forEach(u => next.add(u));
                    }
                    return next;
                  });
                }}
                className="text-xs px-3 py-1.5 rounded-full border transition-colors"
                style={{
                  borderColor: allGroupSelected ? '#0a7a72' : '#E2E8F0',
                  background: allGroupSelected ? '#F0FDFA' : '#fff',
                  color: allGroupSelected ? '#0a7a72' : '#64748B',
                }}
              >
                {prefix} ({groupUrls.length}件){allGroupSelected ? ' ✓' : ''}
              </button>
            );
          })}
        </div>
      )}

      {/* URL一覧 */}
      <div className="max-h-72 overflow-y-auto mb-6 border border-t-0 border-border rounded-b-lg">
        {discoveredUrls.map((item) => {
          const { name, path } = getUrlDisplay(item.url);
          return (
            <div
              key={item.url}
              className="flex items-center gap-3 px-4 py-3 bg-white border-t border-border hover:bg-gray-50 cursor-pointer"
              onClick={() => handleToggleUrl(item.url)}
            >
              <input
                type="checkbox"
                checked={selectedUrls.has(item.url)}
                onChange={() => handleToggleUrl(item.url)}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 cursor-pointer flex-shrink-0"
                style={{ accentColor: '#0a7a72' }}
              />
              <span className="flex-1 text-sm text-text-primary truncate">{name}</span>
              <span className="text-xs text-text-secondary flex-shrink-0 ml-2">{path}</span>
            </div>
          );
        })}
      </div>

      {/* アクションバー */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-text-secondary">選択したページをAIが学習します</span>
        <Button
          data-testid="start-learning-btn"
          onClick={handleStartLearning}
          disabled={selectedUrls.size === 0 || (phase as string) === 'crawling'}
          style={{ backgroundColor: '#0a7a72' }}
        >
          {(phase as string) === 'crawling' ? '処理中...' : `${selectedUrls.size}ページを学習する`}
        </Button>
      </div>
    </div>
  );
};

export default UrlDiscovery;
